// Dialog that shows the decoded body of one HTTP request/response pair
// captured by the per-job TLS MITM proxy. The event payload embeds up
// to 64 KiB per direction (base64); bigger bodies have truncated=true
// and the row still reports the full size.
//
//   <HttpBodyDialog open row onClose />
//
// Rendering strategy: if the bytes look like printable UTF-8 (common
// cases: JSON, HTML, form bodies, text/plain), show as plain text. Else
// fall back to a classic 16-byte hex view. The user usually wants text;
// hex is the escape hatch for binary C2 payloads.
import { useEffect, useMemo, useRef } from "react";
import type { HttpRow } from "../derive/hosts";

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Returns a text decode of `bytes` if ≥95% of bytes are printable ASCII
 *  or valid UTF-8, else null (caller falls through to hex). The 95%
 *  cutoff keeps JSON with trailing \r\n and headers with a stray binary
 *  byte on the text path, while truly binary bodies land in hex. */
function tryText(bytes: Uint8Array): string | null {
  if (bytes.length === 0) return "";
  let printable = 0;
  for (let i = 0; i < Math.min(bytes.length, 4096); i++) {
    const b = bytes[i];
    if ((b >= 0x20 && b <= 0x7e) || b === 0x09 || b === 0x0a || b === 0x0d) printable++;
  }
  const ratio = printable / Math.min(bytes.length, 4096);
  if (ratio < 0.95) return null;
  try {
    return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  } catch {
    return null;
  }
}

function HexDump({ bytes }: { bytes: Uint8Array }) {
  const CAP = 4 * 1024;
  const shown = bytes.length > CAP ? bytes.subarray(0, CAP) : bytes;
  const rows: string[] = [];
  for (let i = 0; i < shown.length; i += 16) {
    const chunk = shown.subarray(i, i + 16);
    const hex = Array.from(chunk, (b) => b.toString(16).padStart(2, "0")).join(" ");
    const ascii = Array.from(chunk, (b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : ".")).join("");
    rows.push(`${i.toString(16).padStart(8, "0")}  ${hex.padEnd(48, " ")}  ${ascii}`);
  }
  return (
    <pre className="m-0 whitespace-pre p-3 font-mono text-[11.5px] leading-[1.45] tabular-nums text-ink">
      {rows.join("\n")}
    </pre>
  );
}

function BodyPane({ label, b64, truncated, fullSize }: { label: string; b64?: string; truncated?: boolean; fullSize?: number }) {
  const bytes = useMemo(() => (b64 ? b64ToBytes(b64) : null), [b64]);
  if (!bytes) {
    return (
      <div className="rounded border border-rule-strong bg-paper p-3 text-meta text-ink-mute">
        <span className="lens-eyebrow">{label}</span>
        <div className="mt-1">No body captured for this direction.</div>
      </div>
    );
  }
  const text = tryText(bytes);
  return (
    <div className="rounded border border-rule-strong bg-paper">
      <div className="flex items-center justify-between border-b border-rule-strong px-3 py-2">
        <span className="lens-eyebrow">{label}</span>
        <span className="text-meta text-ink-mute tabular-nums">
          {bytes.length.toLocaleString()} bytes
          {truncated && fullSize != null && ` · showing first ${bytes.length.toLocaleString()} of ${fullSize.toLocaleString()}`}
          {!text && " · binary"}
        </span>
      </div>
      <div className="tscroll max-h-[48vh] overflow-auto">
        {text != null ? (
          <pre className="m-0 whitespace-pre-wrap break-all p-3 font-mono text-[12px] leading-[1.45] text-ink">{text}</pre>
        ) : (
          <HexDump bytes={bytes} />
        )}
      </div>
    </div>
  );
}

export function HttpBodyDialog({ open, row, onClose }: { open: boolean; row: HttpRow | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);
  const title = row ? `${row.method} ${row.url}` : "";
  const status = row?.statuses[0];
  return (
    <dialog
      ref={ref}
      aria-labelledby="http-body-title"
      onClose={onClose}
      onKeyDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="m-auto w-[min(1080px,calc(100vw-32px))] border border-rule-strong bg-raised p-0 text-ink shadow-pop backdrop:bg-paper/70 open:transition-[opacity,translate] open:duration-160 open:ease-out starting:open:translate-y-1 starting:open:opacity-0"
    >
      <div className="px-6 pt-5 pb-6">
        <p className="lens-eyebrow">HTTP · body contents</p>
        <h2 id="http-body-title" className="mt-1 text-h3 text-ink break-all" translate="no">
          {title}
        </h2>
        {status != null && (
          <p className="mt-1 text-ui text-ink-soft">
            status {status} · {row?.count ?? 1} request{(row?.count ?? 1) === 1 ? "" : "s"} aggregated · showing most recent
          </p>
        )}
        <div className="mt-5 min-w-0 space-y-4">
          <BodyPane label="Request body" b64={row?.reqBodyB64} truncated={row?.reqBodyTruncated} fullSize={row?.reqBodyFullSize} />
          <BodyPane label="Response body" b64={row?.respBodyB64} truncated={row?.respBodyTruncated} fullSize={row?.respBodyFullSize} />
        </div>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="border border-rule-field bg-paper px-4 py-2 text-ui text-ink hover:border-ink focus:border-ink focus:outline-none"
          >
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}
